import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { doc, updateDoc } from 'firebase/firestore';

function ChangeEmail() {
  const { currentUser, verifyBeforeUpdateEmail, reauthenticateUser } = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isPendingVerification, setIsPendingVerification] = useState(false);
  
  const [formData, setFormData] = useState({
    currentPassword: '',
    newEmail: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);

    if (!currentUser) {
      setError('ログインが必要です。');
      setSubmitting(false);
      return;
    }

    // バリデーション
    if (!formData.currentPassword) {
      setError('現在のパスワードを入力してください。');
      setSubmitting(false);
      return;
    }

    if (!formData.newEmail) {
      setError('新しいメールアドレスを入力してください。');
      setSubmitting(false);
      return;
    }

    // メールアドレス形式のバリデーション
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.newEmail)) {
      setError('有効なメールアドレスを入力してください。');
      setSubmitting(false);
      return;
    }

    if (formData.newEmail === currentUser?.email) {
      setError('現在のメールアドレスと同じです。');
      setSubmitting(false);
      return;
    }
      try {
      // パスワードで再認証
      await reauthenticateUser(formData.currentPassword);
      
      // メールアドレス更新前に確認を開始
      await verifyBeforeUpdateEmail(formData.newEmail);
      
      // Firestoreのユーザードキュメントも更新
      await updateDoc(doc(db, 'users', currentUser.uid), {
        email: formData.newEmail,
        updatedAt: new Date()
      });

      setSuccess('確認メールを送信しました。新しいメールアドレスを確認してください。');
      setIsPendingVerification(true);
      
    } catch (error) {
      console.error('メールアドレスの変更に失敗しました:', error);
      
      if (error.code === 'auth/wrong-password') {
        setError('現在のパスワードが正しくありません。');
      } else if (error.code === 'auth/email-already-in-use') {
        setError('このメールアドレスは既に使用されています。');
      } else if (error.code === 'auth/invalid-email') {
        setError('有効なメールアドレスを入力してください。');
      } else {
        setError('メールアドレスの変更に失敗しました。もう一度お試しください。');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/profile');
  };

  return (
    <div className="change-email-container">
      {submitting && (
        <div className="loading-overlay">
          <div className="loading-spinner" />
          変更中...
        </div>
      )}
      <div className="card">
        <h1 className="page-title">メールアドレス変更</h1>
        
        {error && <div className="alert alert-danger">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}
        
        {!isPendingVerification ? (
          <form onSubmit={handleSubmit} className="change-form">
            <div className="current-info">
              <div className="info-item">
                <label className="form-label">現在のメールアドレス</label>
                <p className="current-email">
                  {currentUser?.email}
                  <span className="security-badge">🔒 保護済み</span>
                </p>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">新しいメールアドレス *</label>
              <input
                type="email"
                name="newEmail"
                className="form-control"
                value={formData.newEmail}
                onChange={handleChange}
                required
                placeholder="new-email@example.com"
              />
            </div>

            <div className="form-group">
              <label className="form-label">現在のパスワード *</label>
              <input
                type="password"
                name="currentPassword"
                className="form-control"
                value={formData.currentPassword}
                onChange={handleChange}
                required
                placeholder="現在のパスワードを入力してください"
              />
            </div>

            <div className="notice">
              <p>⚠️ セキュリティのため、現在のパスワードでの再認証が必要です。</p>
              <p>📧 確認メールが送信されます。新しいメールアドレスを確認してください。</p>
              <p>🔒 確認が完了するまで、現在のメールアドレスが有効です。</p>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancel}
                disabled={submitting}
              >
                キャンセル
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitting}
              >
                {submitting ? '変更中...' : '変更'}
              </button>
            </div>
          </form>
        ) : (
          <div className="verification-pending">
            <div className="pending-icon">📧</div>
            <h2>確認メールを送信しました</h2>
            <p>新しいメールアドレス {formData.newEmail} に確認メールを送信しました。</p>
            <p>メール内のリンクをクリックして、メールアドレス変更を完了してください。</p>
            <div className="pending-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setIsPendingVerification(false);
                  setSuccess('');
                }}
              >
                別のメールアドレスを試す
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => navigate('/profile')}
              >
                後で行う
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ChangeEmail;
